import { useState, useEffect } from 'react';
import TemplateEditorModal from './TemplateEditorModal';
import ConfirmModal from './ConfirmModal';
import { ChevronLeftIcon, PlusIcon, DeleteIcon, TemplateIcon, DownloadIcon, LoaderIcon } from './Icons';
import { TYPE_CONFIG } from '../constants';
import api from '../api';

export default function TemplatesView({ onBack, showToast, userProfile, sprintId, onItemsImported, projectType }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importing, setImporting] = useState(null);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.getTemplates();
      setTemplates(data);
    } catch (err) {
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteTemplate(deleteTarget.id);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
      showToast('Template deleted', 'success');
    } catch (err) {
      showToast('Failed to delete template', 'error');
    }
  };

  const handleImport = async (template) => {
    const tType = template.templateType || 'scrum';
    if (projectType && tType !== projectType) {
      showToast(`This template is for ${tType === 'scrum' ? 'Scrum' : 'Kanban'} projects only`, 'error');
      return;
    }
    setImporting(template.id);
    try {
      const created = await api.importTemplate(template.id, sprintId, projectType);
      showToast(`Imported ${created.length} items from "${template.name}"`, 'success');
      if (onItemsImported) onItemsImported(created);
    } catch (err) {
      showToast(err.message || 'Failed to import template', 'error');
    } finally {
      setImporting(null);
    }
  };

  const handleSaved = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    loadTemplates();
  };

  const getItemTypeSummary = (items) => {
    if (!items?.length) return 'Empty';
    const counts = {};
    items.forEach((i) => { counts[i.type] = (counts[i.type] || 0) + 1; });
    return Object.entries(counts)
      .map(([type, count]) => `${count} ${TYPE_CONFIG[type]?.label || type}${count > 1 ? 's' : ''}`)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="app-loading">
        <LoaderIcon size={32} />
        <p>Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="templates-wrapper">
      <div className="templates-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ChevronLeftIcon size={16} /> Back
        </button>
        <h2><TemplateIcon size={20} /> Templates</h2>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditingTemplate(null); setShowEditor(true); }}>
            <PlusIcon size={16} /> New Template
          </button>
        )}
      </div>

      {templates.length === 0 ? (
        <div className="templates-empty">
          <TemplateIcon size={48} />
          <p>No templates yet.</p>
          {isAdmin && <p>Create a template to define reusable hierarchy structures.</p>}
        </div>
      ) : (
        <div className="templates-list">
          {templates
            .filter((t) => !projectType || (t.templateType || 'scrum') === projectType)
            .map((template) => {
            const tType = template.templateType || 'scrum';
            return (
              <div key={template.id} className="template-card">
                <div className="template-card-info" onClick={() => { if (isAdmin) { setEditingTemplate(template); setShowEditor(true); } }}>
                  <h3 className="template-card-name">
                    <TemplateIcon size={16} /> {template.name}
                    <span className={`template-type-badge template-type-badge--${tType}`}>
                      {tType === 'scrum' ? 'Scrum' : 'Kanban'}
                    </span>
                  </h3>
                  {template.description && (
                    <p className="template-card-desc">{template.description}</p>
                  )}
                  <span className="template-card-summary">
                    {getItemTypeSummary(template.items)}
                  </span>
                </div>
                <div className="template-card-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleImport(template)}
                    disabled={importing === template.id}
                  >
                    {importing === template.id ? (
                      <><LoaderIcon size={14} /> Importing...</>
                    ) : (
                      <><DownloadIcon size={14} /> Import</>
                    )}
                  </button>
                  {isAdmin && (
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(template)}>
                      <DeleteIcon size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showEditor && (
        <TemplateEditorModal
          template={editingTemplate}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
          onSaved={handleSaved}
          showToast={showToast}
          userProfile={userProfile}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message={`Delete template "${deleteTarget.name}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
